<script setup lang="ts">
import { DatetimePopup, useClickOutside, useDisclosure, UserAvatar, useTimeSinceString } from '#shared'
import { computed, capitalize, useTemplateRef } from 'vue'
import UserProfilePreview from '#features/user-profile-preview/ui/user-profile-preview.vue'
import type { Message } from '#entities/message'

const { message, variant = 'regular' } = defineProps<{
  message: Message
  variant?: 'regular' | 'compact'
  isHighlighted?: boolean
}>()

const avatarSize = computed(() => (variant === 'regular' ? 'medium' : 'small'))
const avatarWrapEl = useTemplateRef('avatarWrapEl')
const userProfileDisclosure = useDisclosure()
const onUserClick = () => userProfileDisclosure.open()

const sendDatetimeString = useTimeSinceString(message.createdAt)
const datetimeEl = useTemplateRef('datetimeEl')
const datetimePopupDisclosure = useDisclosure()
const datetimePopupHorizontalPosition = computed(() => (variant === 'regular' ? 'center' : 'right-outside'))
const datetimePopupVerticalPosition = computed(() => (variant === 'regular' ? 'top-outside' : 'center'))
useClickOutside(
  computed(() => [datetimeEl.value]),
  () => datetimePopupDisclosure.close()
)
</script>

<template>
  <div
    :class="[$style.root, $style[`root${capitalize(variant)}`], { [$style.rootHighlighted]: isHighlighted }]"
    :data-message-id="message.id"
    ref="rootEl"
  >
    <div v-if="$slots.preContent" :class="[$style.preContent, $style[`preContent${capitalize(variant)}`]]">
      <slot name="preContent" />
    </div>

    <div :class="$style.content">
      <div :class="$style.avatarWrap" ref="avatarWrapEl">
        <button :class="$style.avatarButton" @click="onUserClick">
          <UserAvatar :user="message.sender" :size="avatarSize" />
        </button>
      </div>

      <div :class="[$style.main, $style[`main${capitalize(variant)}`]]">
        <div :class="$style.header">
          <button :class="[$style.senderName, $style['senderName' + capitalize(variant)]]" @click="onUserClick">
            {{ message.sender.displayName }}
          </button>

          <div
            :class="$style.datetime"
            ref="datetimeEl"
            @mouseenter="datetimePopupDisclosure.open"
            @mouseleave="datetimePopupDisclosure.close"
            @click="datetimePopupDisclosure.open"
          >
            {{ sendDatetimeString }}
          </div>

          <DatetimePopup
            :date="message.createdAt"
            :targetEl="datetimeEl"
            :isShown="datetimePopupDisclosure.isOpen"
            :verticalPosition="datetimePopupVerticalPosition"
            :horizontalPostion="datetimePopupHorizontalPosition"
          />

          <slot name="header" />
        </div>

        <slot name="main" />
      </div>
    </div>

    <UserProfilePreview
      :isOpen="userProfileDisclosure.isOpen"
      :user="message.sender"
      :targetEl="avatarWrapEl"
      :verticalOffset="10"
      @close="userProfileDisclosure.close"
    />

    <slot name="overlay" />
  </div>
</template>

<style lang="scss" module>
@use '#mixins/media' as media;

.root {
  display: flex;
  flex-direction: column;
  transition: background-color 0.3s;
  position: relative;

  &:hover {
    background-color: #1a1920;
  }

  &:last-child {
    .separator {
      display: none;
    }
  }
}

.rootRegular {
  padding: 0 20px;

  @include media.mobile-large {
    padding: 0 15px;
  }

  @include media.mobile {
    padding: 0 10px;
  }
}

.rootCompact {
  padding: 0 10px;
}

.rootHighlighted {
  background-color: #2b2d4b80;
}

.preContentRegular {
  margin: 0 -20px;

  @include media.mobile-large {
    margin: 0 -15px;
  }

  @include media.mobile {
    margin: 0 -10px;
  }
}

.preContentCompact {
  margin: 0 -10px;
}

.content {
  display: flex;
  gap: 10px;
}

.avatarWrap {
  padding: 10px 0;
  width: fit-content;
  flex-shrink: 0;

  @include media.mobile {
    padding: 5px 0;
  }
}

.avatarButton {
  border: none;
  display: flex;
  background-color: transparent;
  padding: 0;
}

.main {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.mainRegular {
  padding: 15px 0;
  gap: 5px;

  @include media.mobile-large {
    padding: 10px 0;
  }

  @include media.mobile {
    gap: 0;
    padding: 5px 0;
  }
}

.mainCompact {
  padding: 5px 0;
  gap: 0;
}

.header {
  display: flex;
  align-items: first baseline;
  gap: 5px;
  flex-wrap: wrap;
}

.senderName {
  font-size: 16px;
  font-weight: 500;
  background-color: transparent;
  padding: 0;
  border: none;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #86b2ff;
}

.senderNameRegular {
  font-size: 16px;

  @include media.mobile {
    font-size: 15px;
  }
}

.senderNameCompact {
  font-size: 15px;
}

.datetime {
  font-size: 12px;
  opacity: 0.5;
}
</style>
